import React, { useState, useEffect, useMemo } from "react";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import { Card, CardBody, CardHeader, Col, Row } from "react-bootstrap"
import {
    adminApi,
    deleteAdminApi,
    adminStatusChangesApi,
    roleActiveListApi
} from "store/actions";
import Table from "components/Table";
import Columns from "./Columns";
import FormikForm from "components/shared/FormikForm";
import AddUpdateAdmin from "components/Modals/AddUpdateAdmin";
import ViewAdminModal from "components/Modals/ViewAdminDetail";
import PermissionGate from "components/PermissionGate";
import usePermission from "hooks/usePermission";

const initialValuesForFilter = {
    search: "",
    roleId: "",
    status: "",
};

const List = () => {
    const dispatch = useDispatch();

    const { adminList, totalAdmin, loading, totalSuperAdminCount, totalAdminCount, activeCount, blockedCount, } = useSelector((state) => state.Admin,);

    const [columns, setcolumns] = useState([{ dataField: "", text: "" }]);
    const [roleOptions, setRoleOptions] = useState([]);

    const [showAddUpdatePop, setShowAddUpdatePop] = useState({
        show: false,
        rowData: null,
    });
    const [viewAdminId, setViewAdminId] = useState(null);

    const [filter, setFilter] = useState({
        page: 1,
        limit: 20,
        order: -1,
        orderBy: "createdAt",
        search: "",
        roleId: "",
        status: "",
    });

    const { hasPermission } = usePermission();
    const permissions = useMemo(() => ({
        add: hasPermission("add_admin"),
        edit: hasPermission("edit_admin"),
        delete: hasPermission("delete_admin"),
    }), [hasPermission]);

    // Fetch role list once, on mount
    useEffect(() => {
        dispatch(
            roleActiveListApi({
                callBack: (err, res) => {
                    if (err) return;
                    const options = (res?.data || []).map((role) => ({
                        value: role._id,
                        label: role.roleName,
                    }));
                    setRoleOptions(options);
                },
            })
        );
    }, []);

    // Build search fields dynamically so role options stay in sync
    const InputFieldsForSearch = useMemo(() => ([
        {
            name: "search",
            type: "text",
            label: "",
            placeholder: "Search...",
        },
        {
            name: "roleId",
            type: "select",
            label: "",
            placeholder: "Select Role Name",
            options: roleOptions,
        },
        {
            name: "status",
            type: "select",
            label: "",
            placeholder: "Select status",
            options: [
                { value: "active", label: "Active" },
                { value: "blocked", label: "Blocked" },
            ],
        },
    ]), [roleOptions]);

    const handleAdminStatus = (row, status) => {
        try {
            const callBack = (err, res) => {
                if (err) return;
                getApiData(filter);
            };

            const payload = {
                _id: row?._id,
                status,
            };

            dispatch(
                adminStatusChangesApi({
                    data: payload,
                    callBack,
                }),
            );
        } catch (error) {
            console.error(error, "<===err");
        }
    };

    useEffect(() => {
        getApiData(filter);
    }, [filter]);

    useEffect(() => {
        setcolumns(
            Columns({
                handleEditHandler,
                handleAdminStatus,
                deleteHandler,
                handleViewHandler,
                permissions,
            })?.filter((item) => {
                if (
                    item?.text === "Action" &&
                    !permissions.edit &&
                    !permissions?.delete
                ) {
                    return false;
                }
                return true;
            }),
        );
    }, [permissions.edit, permissions.delete]);

    async function getApiData(query) {
        try {
            dispatch(adminApi({ data: query }));
        } catch (error) { }
    }

    const handleFilterForm = (info) => {
        setFilter({
            ...filter,
            page: 1,
            limit: 20,
            order: -1,
            orderBy: "createdAt",
            search: info.search,
            roleId: info.roleId,
            status: info.status,
        });
    };

    const handleEditHandler = (info) => {
        setShowAddUpdatePop({
            show: true,
            rowData: info,
        });
    };

    const deleteHandler = (id) => {
        try {
            const userConfirmed = window.confirm(
                "Are you sure, you want to delete this admin?",
            );
            if (userConfirmed) {
                const callBack = (err, res) => {
                    if (err) {
                    } else {
                        getApiData(filter);
                    }
                };

                const payload = {
                    _id: id
                };

                dispatch(
                    deleteAdminApi({
                        data: payload,
                        callBack,
                    }),
                );
            }
        } catch (error) {
            console.error(error, "<===err");
        }
    };

    const handleViewHandler = (row) => {
        setViewAdminId(row._id);
    };

    const handleConfirmAddUpdate = () => {
        setShowAddUpdatePop({ show: false, rowData: null });
        getApiData(filter); // just refresh the list
    };

    return (
        <>
            {loading && (
                <div className="main-loader">
                    <FadeLoader size={1000} />
                </div>
            )}
            <Helmet title={"Admin"} />
            {showAddUpdatePop.show && (
                <AddUpdateAdmin
                    show={showAddUpdatePop.show}
                    loading={loading}
                    handleClose={() =>
                        setShowAddUpdatePop({ show: false, rowData: null })
                    }
                    handleConfirm={handleConfirmAddUpdate}
                    rowData={showAddUpdatePop?.rowData}
                />
            )}

            {viewAdminId && (
                <ViewAdminModal
                    show={!!viewAdminId}
                    id={viewAdminId}
                    handleClose={() => setViewAdminId(null)}
                />
            )}

            <section className="section">
                <div className="section-header">
                    <h1>Admin</h1>
                </div>
                <Row>
                    <Col lg="3" md="4" sm="6" className="my-2">
                        <Card className="my-2">
                            <CardHeader>Total Super Admin</CardHeader>
                            <CardBody className="spinner-content">{totalSuperAdminCount} </CardBody>
                        </Card>
                    </Col>
                    <Col lg="3" md="4" sm="6" className="my-2">
                        <Card className="my-2">
                            <CardHeader>Total Admin</CardHeader>
                            <CardBody className="spinner-content"> {totalAdminCount}</CardBody>
                        </Card>
                    </Col>
                    <Col lg="3" md="4" sm="6" className="my-2">
                        <Card className="my-2">
                            <CardHeader>Active Admin</CardHeader>
                            <CardBody className="spinner-content">{activeCount}</CardBody>
                        </Card>
                    </Col>
                    <Col lg="3" md="4" sm="6" className="my-2">
                        <Card className="my-2">
                            <CardHeader>Blocked Admin</CardHeader>
                            <CardBody className="spinner-content">{blockedCount}</CardBody>
                        </Card>
                    </Col>
                </Row>
                <div className="section-body">
                    <div className="card">
                        <div className="row justify-content-between">
                            <div className="col-6">
                                <div className="card-header">
                                    <h2 className="section-title">Admin List</h2>
                                </div>
                            </div>
                            <PermissionGate permission="add_admin">
                                <div className="col-6">
                                    <div className="card-header d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-primary"
                                            onClick={() =>
                                                setShowAddUpdatePop({
                                                    show: true,
                                                    rowData: null,
                                                })
                                            }
                                        >
                                            Add Admin
                                        </button>
                                    </div>
                                </div>
                            </PermissionGate>
                            <div className="col-12">
                                <div className="card-header">
                                    <FormikForm
                                        initialValues={initialValuesForFilter}
                                        onSubmit={handleFilterForm}
                                        loading={loading}
                                        inputFields={InputFieldsForSearch}
                                        buttonText={"Search"}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {adminList?.length > 0 && (
                        <Table
                            data={adminList}
                            columns={columns}
                            setFilter={setFilter}
                            total={totalAdmin}
                            filter={filter}
                        />
                    )}
                    {!loading && adminList?.length === 0 && (
                        <p style={{ textAlign: "center" }}>No Admin Found</p>
                    )}
                </div>
            </section>
        </>
    );
};

export default List;